/*
 * Copyright 2026 Sony Corporation
 */

import type { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import type { ArcRotateCameraPointersInput } from '@babylonjs/core/Cameras/Inputs/arcRotateCameraPointersInput';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';

import type { Bounds } from './types';

/** Radians per pixel of left-drag. */
const LOOK_SPEED = 0.004;
/** Radians per pixel of right-drag, before the orbit's inertia adds its coast. A third of Babylon's default, and inverted: the scene follows the hand. */
const ORBIT_SPEED = -1 / 3000;
/** Share of the distance to what is under the cursor that one wheel notch covers. */
const WHEEL_STEP = 0.15;
/** Never slower than this share of the scene's radius per notch, or a surface could be approached forever. */
const MIN_STEP = 0.02;
/** A wheel notch in pixels, and in lines, as browsers report `deltaY`. */
const NOTCH_PIXELS = 100;
const NOTCH_LINES = 3;
/** Babylon's own beta limits for the orbit camera. */
const BETA_MARGIN = 0.01;

const LEFT = 0;
const MIDDLE = 1;
const RIGHT = 2;

/**
 * Where the camera is after turning it in place: the same position, looking along (alpha, beta).
 *
 * An orbit camera is placed at `target + radius * offset(alpha, beta)`, so
 * turning the head means moving the target, not the camera.
 */
export const lookFrom = (position: Vector3, alpha: number, beta: number, radius: number): Vector3 =>
  position.subtract(offsetOf(alpha, beta).scale(radius));

const offsetOf = (alpha: number, beta: number): Vector3 =>
  new Vector3(Math.cos(alpha) * Math.sin(beta), Math.cos(beta), Math.sin(alpha) * Math.sin(beta));

/** The eye from the camera's own terms: `position` lags a frame behind a target moved in place. */
const eyeOf = (camera: ArcRotateCamera): Vector3 =>
  camera.target.add(offsetOf(camera.alpha, camera.beta).scale(camera.radius));

/** How far one wheel event moves the camera: a share of the distance ahead, never less than a floor. */
export const wheelStep = (deltaY: number, deltaMode: number, ahead: number, sceneRadius: number): number => {
  const notches = deltaY / (deltaMode === 1 ? NOTCH_LINES : NOTCH_PIXELS);
  return -notches * Math.max(ahead * WHEEL_STEP, sceneRadius * MIN_STEP);
};

/**
 * Walk-through mouse controls over the orbit camera.
 *
 * - left-drag: look around from where the camera stands;
 * - right-drag: orbit;
 * - wheel: move toward what is under the cursor, or away from it, without
 *   the orbit camera's stop short of its target, which kept the wheel from
 *   ever taking anyone through a room;
 * - middle-drag, or Ctrl + right-drag: pan.
 */
export const attachExploreControls = (
  camera: ArcRotateCamera,
  canvas: HTMLCanvasElement,
  scene: Scene,
  bounds: () => Bounds,
  /** The first surface under a canvas point, where the scene's own picking cannot see all of them (splats). */
  surfaceAt: () => ((x: number, y: number) => Vector3 | undefined) | undefined
): (() => void) => {
  camera.attachControl(true);
  // Babylon 9 maps buttons to interactions here, not in the pointer input. Every gizmo drag
  // re-attaches the camera with the defaults, which sets the first unmodified pointer pan rule to
  // the right button; the shadowed right-button pan rule is there to take that rewrite.
  camera.movement.input.inputMap = [
    { source: 'pointer', button: RIGHT, modifiers: { ctrl: true }, interaction: 'pan' },
    { source: 'pointer', button: RIGHT, interaction: 'rotate', sensitivity: ORBIT_SPEED },
    { source: 'pointer', button: RIGHT, interaction: 'pan' },
    { source: 'pointer', button: MIDDLE, interaction: 'pan' },
    ...camera.movement.input.inputMap.filter((entry) => entry.source !== 'pointer' && entry.source !== 'wheel'),
  ];
  (camera.inputs.attached.pointers as ArcRotateCameraPointersInput).buttons = [MIDDLE, RIGHT];
  camera.inputs.removeByType('ArcRotateCameraMouseWheelInput');

  let looking: { id: number; x: number; y: number } | undefined;
  // A gizmo drag detaches the camera's own input for its duration; the look must stand aside too.
  const gizmoDragging = () => !camera.inputs.attachedToElement;

  const onDown = (event: PointerEvent) => {
    if (event.button === MIDDLE) {
      // Linux pastes, other platforms start autoscroll.
      event.preventDefault();
    }
    if (event.button !== LEFT || gizmoDragging()) {
      return;
    }
    looking = { id: event.pointerId, x: event.clientX, y: event.clientY };
    canvas.setPointerCapture(event.pointerId);
  };

  const onMove = (event: PointerEvent) => {
    if (!looking || event.pointerId !== looking.id || gizmoDragging()) {
      return;
    }
    const dx = event.clientX - looking.x;
    const dy = event.clientY - looking.y;
    looking = { ...looking, x: event.clientX, y: event.clientY };

    const position = eyeOf(camera);
    // The same signs as orbiting: drag right turns right, drag up looks up.
    const alpha = camera.alpha - dx * LOOK_SPEED;
    const beta = Math.min(Math.max(camera.beta - dy * LOOK_SPEED, BETA_MARGIN), Math.PI - BETA_MARGIN);
    // In place: `setTarget` re-derives the angles from the old position, which turns the view instead.
    camera.target.copyFrom(lookFrom(position, alpha, beta, camera.radius));
    camera.alpha = alpha;
    camera.beta = beta;
  };

  const onUp = (event: PointerEvent) => {
    if (looking && event.pointerId === looking.id) {
      looking = undefined;
      canvas.releasePointerCapture(event.pointerId);
    }
  };

  const onWheel = (event: WheelEvent) => {
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const eye = eyeOf(camera);
    const ray = scene.createPickingRay(x, y, null, camera);
    const surface = surfaceAt()?.(x, y);
    const hit = surface ? undefined : scene.pickWithRay(ray);
    // Nothing under the cursor: the scene's centre is the best guess at how far there is to go.
    const ahead = surface
      ? Vector3.Distance(eye, surface)
      : hit?.hit
        ? hit.distance
        : Vector3.Distance(eye, bounds().center);
    const offset = ray.direction.scale(wheelStep(event.deltaY, event.deltaMode, ahead, bounds().radius));
    // Camera and target together: the view keeps its direction and simply moves.
    camera.target.addInPlace(offset);
  };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onUp);
    canvas.removeEventListener('wheel', onWheel);
  };
};
